import { Box, Card, Divider, Grid, Text, Title } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import ErrorMessage from '../../../components/ErrorMessage';
import Metadata from '../../../components/Metadata';
import PaymentAddButton from '../../../components/Payment/PaymentAddButton';
import Spinner from '../../../components/Spinner';
import { Page } from '../../../constants/pageTitles';
import useFetch from '../../../helpers/useFetch';

const debounceMinutes = 1;
const debouncePerMinute = 5;
const debounceDelay = 5000;
const redirectDelay = 1000;

const PaymentSelectPage = () => {
  const page = `${Page['payment']}`;
  const location = useLocation();

  const status = queryString.parse(location.search)?.status || '';

  const [payment, setPayment] = useState();
  const [paymentStatus, setPaymentStatus] = useState(status === 'failed' ? 'failed' : 'pending');
  const [error, setError] = useState();
  const { accountId } = useParams();
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const fetch = useFetch();

  const generateRedirectUrl = () => {
    let account = user.userRoles
      .filter((role) => role.account)
      .map((role) => role.account)
      .find((account) => account.id === parseInt(accountId));

    let domain = account.domains[0];

    return `/accounts/${accountId}/domains/${domain.id}/settings/account?tab=paymentMethod`;
  };

  const fetchURL = `/api/admin/accounts/${accountId}/paymentMethod`;
  const redirectUrl = generateRedirectUrl();

  useEffect(() => {
    const totalDebounceCount = Math.ceil((debounceMinutes * 60) / debouncePerMinute);
    let debounceCount = 0;
    let statusFetch = 'pending';

    if (status === 'success') {
      const fetchPaymentStatus = async () => {
        try {
          const { payment } = await fetch(fetchURL);

          if (payment?.id) {
            debounceCount = totalDebounceCount;
          }

          statusFetch = payment?.id ? 'successful' : 'pending';
          setPaymentStatus(statusFetch);
          setPayment(payment);
        } catch (error) {
          statusFetch = 'failed';
          setPaymentStatus(statusFetch);
          setError({ message: `Payment request error! ${error.message}` });
        }
      };

      const debouncedFetchPaymentStatus = () => {
        if (debounceCount < totalDebounceCount) {
          fetchPaymentStatus();
          debounceCount++;
          setTimeout(debouncedFetchPaymentStatus, debounceDelay);
        } else {
          if (statusFetch === 'successful') {
            setTimeout(() => navigate(redirectUrl), redirectDelay);
          }

          if (statusFetch === 'pending') {
            statusFetch = 'failed';
            setPaymentStatus(statusFetch);
            setError({ message: 'Payment request timed out' });
            setTimeout(() => navigate(redirectUrl), redirectDelay);
          }
        }
      };

      debouncedFetchPaymentStatus();
    } else {
      fetch(fetchURL)
        .then(({ payment }) => {
          if (payment) {
            setTimeout(() => navigate(redirectUrl), redirectDelay);
          }
          setPayment(payment);
        })
        .catch((error) => {
          setError(error);
        });
    }
  }, [fetch, navigate, fetchURL, redirectUrl, status, paymentStatus]);

  const addPayment = () => {
    fetch(`/api/admin/accounts/${accountId}/paymentMethod/authorization`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
      .then(({ url }) => {
        window.location.replace(url);
      })
      .catch(setError);
  };

  const paymentSuccessfulMessage = (
    <>
      <Card h="auto" withBorder>
        <Title order={3}>
          <IconCheck style={{ color: 'var(--mantine-color-green-6)' }} size={20} /> Payment authorized successfully!
        </Title>
      </Card>
      <Text ta="center" c="dimmed">Redirecting ...</Text>
    </>
  );

  const paymentFailedMessage = (
    <>
      <Card h="auto" withBorder>
        <Title order={3}>
          <IconX style={{ color: 'var(--mantine-color-red-6)' }} size={20} /> There was an error with the payment authorization! Please try
          again.
        </Title>
      </Card>
      <Text ta="center" c="dimmed">Please try again ...</Text>
    </>
  );

  const paymentAvailableMessage = (
    <>
      <Card h="auto" withBorder>
        <Title order={3}>You already have a payment method associated with your account.</Title>
      </Card>
      <Text ta="center" c="dimmed">Redirecting ...</Text>
    </>
  );

  return (
    <>
      <div>
        <Metadata title={page} />
        <Grid pt={60} align="center" h="100%">
          <Grid.Col span={3}></Grid.Col>
          <Grid.Col span={6}>
            <Card withBorder>
              <Box pb="md">
                <Title order={2} fw={700}>{page}</Title>
              </Box>

              <Divider my="sm" />

              {status === 'failed' && error ? paymentFailedMessage : null}

              {status === 'success' && !payment ? (
                <>
                  <Spinner />
                  <Text ta="center" my="md" c="dimmed">Awaiting payment authorization ...</Text>
                </>
              ) : (
                <>
                  {payment ? (
                    <>{paymentStatus === 'successful' ? paymentSuccessfulMessage : paymentAvailableMessage}</>
                  ) : (
                    <>
                      {paymentStatus === 'failed' && paymentFailedMessage}
                      <PaymentAddButton addPayment={addPayment} />
                    </>
                  )}
                </>
              )}
              <Box ta="center">
                <ErrorMessage message={error?.message} />
              </Box>
            </Card>
          </Grid.Col>
          <Grid.Col span={3}></Grid.Col>
        </Grid>
      </div>
    </>
  );
};

export default PaymentSelectPage;
